import { createFileRoute, Outlet, useMatches } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Badge, Card, Input, Table, Td, Th, Spinner } from '@/components/admin/ui';
import {
  BarList,
  DeviceLink,
  EmptyState,
  Pagination,
  SortTh,
} from '@/components';
import type { AdminSortId } from '@/components/SortTh';
import type { BreakdownsResponse } from '@/lib/admin-types';
import { formatAdminDateTime, formatInt } from '@/lib/format';
import { apiJson } from '@/lib/api';

type DeviceItem = {
  installation_id: string;
  nickname: string | null;
  platform: string;
  first_seen_at: string;
  last_seen_at: string;
  unsubscribed_at: string | null;
  app_version: string | null;
  locale: string | null;
  os_version: string | null;
  device_model: string | null;
  brand: string | null;
  hardware_id: string | null;
  system_name: string | null;
  total_memory_mb: number | null;
  cpu_abis: string | null;
  total_disk_mb: number | null;
  screen: string | null;
  is_tablet: boolean | null;
  is_emulator: boolean | null;
  api_level: number | null;
};

type ListRes = { items: DeviceItem[]; total: number; page: number; page_size: number };

type SortKey =
  | 'last_seen_at_desc'
  | 'last_seen_at_asc'
  | 'first_seen_at_desc'
  | 'first_seen_at_asc';

function sortToParts(s: SortKey): { col: AdminSortId; direction: 'asc' | 'desc' } {
  if (s.startsWith('last_seen')) {
    return { col: 'last_seen', direction: s.endsWith('_asc') ? 'asc' : 'desc' };
  }
  return { col: 'first_seen', direction: s.endsWith('_asc') ? 'asc' : 'desc' };
}

function partsToSort(col: AdminSortId, direction: 'asc' | 'desc'): SortKey {
  const b = col === 'last_seen' ? 'last_seen_at' : 'first_seen_at';
  return `${b}_${direction === 'asc' ? 'asc' : 'desc'}` as SortKey;
}

function AdminDevices() {
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [platform, setPlatform] = useState('');
  const [subscribed, setSubscribed] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [sort, setSort] = useState<SortKey>('last_seen_at_desc');

  useEffect(() => {
    const t = setTimeout(() => {
      setQDebounced(q);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const breakdownQ = useQuery({
    queryKey: ['admin', 'breakdowns'],
    queryFn: () => apiJson<BreakdownsResponse>('/v1/admin/analytics/breakdowns'),
  });

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [
      'admin',
      'devices',
      qDebounced,
      platform,
      subscribed,
      page,
      pageSize,
      sort,
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      if (qDebounced.trim()) params.set('q', qDebounced.trim());
      if (platform) params.set('platform', platform);
      if (subscribed === 'y') params.set('subscribed', 'true');
      if (subscribed === 'n') params.set('subscribed', 'false');
      params.set('page', String(page));
      params.set('page_size', String(pageSize));
      params.set('sort', sort);
      return apiJson<ListRes>(`/v1/admin/devices?${params.toString()}`);
    },
  });

  const { col: activeCol, direction } = sortToParts(sort);

  const onHeaderSort = (clicked: AdminSortId) => {
    if (activeCol === clicked) {
      setSort(partsToSort(clicked, direction === 'desc' ? 'asc' : 'desc'));
    } else {
      setSort(partsToSort(clicked, 'desc'));
    }
    setPage(1);
  };

  const platFacet = breakdownQ.data?.by_platform ?? [];
  const osFacet = (breakdownQ.data?.by_os_version ?? []).slice(0, 8);
  const ramFacet = breakdownQ.data?.by_ram_bucket ?? [];
  const hardwareFacet = (breakdownQ.data?.by_hardware_id ?? []).slice(0, 8);

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold tracking-tight">Devices</h1>
      <div className="flex flex-col gap-6 lg:flex-row">
        <aside className="w-full shrink-0 space-y-4 lg:w-56">
          <Card padding="md">
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground">
              Quick filters
            </h2>
            <div className="mb-3 flex flex-wrap gap-2">
              <button type="button" onClick={() => setSubscribed(subscribed === 'y' ? '' : 'y')}>
                <Badge variant={subscribed === 'y' ? 'primary' : 'neutral'}>Notifications on</Badge>
              </button>
              <button type="button" onClick={() => setSubscribed(subscribed === 'n' ? '' : 'n')}>
                <Badge variant={subscribed === 'n' ? 'danger' : 'neutral'}>Notifications off</Badge>
              </button>
            </div>
            <p className="mb-1 text-xs font-medium text-nova-muted-foreground">Platform (click)</p>
            <ul className="space-y-1 text-sm">
              <li>
                <button
                  type="button"
                  className={`w-full rounded-lg px-2 py-1 text-left hover:bg-nova-accent ${
                    platform === '' ? 'font-semibold text-nova-primary' : 'text-nova-foreground'
                  }`}
                  onClick={() => {
                    setPlatform('');
                    setPage(1);
                  }}
                >
                  All platforms
                </button>
              </li>
              {platFacet.map((p) => (
                <li key={p.key || 'unknown'}>
                  <button
                    type="button"
                    className={`w-full rounded-lg px-2 py-1 text-left hover:bg-nova-accent ${
                      platform === (p.key || '') ? 'font-semibold text-nova-primary' : 'text-nova-foreground'
                    }`}
                    onClick={() => {
                      setPlatform(p.key || '');
                      setPage(1);
                    }}
                  >
                    {p.key || '(unknown)'}{' '}
                    <span className="text-nova-muted-foreground">({formatInt(p.count)})</span>
                  </button>
                </li>
              ))}
            </ul>
          </Card>
          <Card padding="md">
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground">
              OS version (top)
            </h2>
            <BarList data={osFacet} emptyLabel="—" />
          </Card>
          <Card padding="md">
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground">
              RAM
            </h2>
            <BarList data={ramFacet} emptyLabel="—" />
          </Card>
          <Card padding="md">
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground">
              Hardware (top)
            </h2>
            <BarList data={hardwareFacet} emptyLabel="—" />
          </Card>
        </aside>

        <Card className="min-w-0 flex-1">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1">
              <Input
                label="Search (nickname or installation id)"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="type to filter"
              />
            </div>
            <div className="w-full sm:w-32">
              <Input
                label="Platform"
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                placeholder="ios"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm text-nova-muted-foreground">Notifications</label>
              <select
                className="w-full rounded-lg border border-nova-border h-8 px-2.5 text-sm"
                value={subscribed}
                onChange={(e) => {
                  setSubscribed(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">Any</option>
                <option value="y">On</option>
                <option value="n">Off</option>
              </select>
            </div>
          </div>

          {isLoading && (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          )}
          {isError && (
            <p className="text-sm text-red-600">
              {(error as Error).message}
              <button
                type="button"
                className="ml-2 underline"
                onClick={() => {
                  void refetch();
                }}
              >
                Retry
              </button>
            </p>
          )}
          {data && data.items.length === 0 && <EmptyState title="No devices match" />}
          {data && data.items.length > 0 && (
            <>
              <div className="max-w-full overflow-x-auto">
                <Table>
                  <thead>
                    <tr>
                      <SortTh
                        sortId="last_seen"
                        label="Last seen"
                        active={activeCol}
                        direction={direction}
                        onClick={onHeaderSort}
                      />
                      <SortTh
                        sortId="first_seen"
                        label="First seen"
                        active={activeCol}
                        direction={direction}
                        onClick={onHeaderSort}
                      />
                      <Th>ID</Th>
                      <Th>Nickname</Th>
                      <Th>Platform</Th>
                      <Th>Notifications</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((d) => (
                      <tr key={d.installation_id}>
                        <Td className="whitespace-nowrap text-nova-muted-foreground">
                          {formatAdminDateTime(d.last_seen_at)}
                        </Td>
                        <Td className="whitespace-nowrap text-nova-muted-foreground">
                          {formatAdminDateTime(d.first_seen_at)}
                        </Td>
                        <Td className="whitespace-nowrap">
                          <DeviceLink installationId={d.installation_id} label="short" />
                        </Td>
                        <Td>
                          {d.nickname ? (
                            <DeviceLink
                              installationId={d.installation_id}
                              nickname={d.nickname}
                              label="nickname"
                            />
                          ) : (
                            '—'
                          )}
                        </Td>
                        <Td>{d.platform}</Td>
                        <Td>{d.unsubscribed_at == null ? 'on' : 'off'}</Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <Pagination
                page={data.page}
                pageSize={data.page_size}
                total={data.total}
                onChange={setPage}
              />
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

/**
 * Parent for `/admin/devices` (list) and `/admin/devices/$installationId`.
 * Without an Outlet, a device link keeps the list mounted.
 */
function DevicesLayout() {
  const matches = useMatches();
  const leafId = matches.at(-1)?.routeId;
  if (leafId === '/admin/devices/$installationId') {
    return <Outlet />;
  }
  return <AdminDevices />;
}

export const Route = createFileRoute('/admin/devices')({ component: DevicesLayout });
